import { GrowthBook } from "@growthbook/growthbook";
import {
  thirdPartyTrackingPlugin,
  autoAttributesPlugin,
} from "@growthbook/growthbook/plugins";
import { env } from "../config/env";
import { memoryStore } from "../lib/store";

const KEYS = {
  production: "sdk-MEf1NyqLX7nlZqyY",
  staging: "sdk-oFEakg1FAQCWpqMp",
  development: "sdk-hwis5IH8TK82x",
} as const satisfies Record<typeof env.appEnv, string>;

const environment = env.appEnv;

const GROWTHBOOK_CLIENT_KEY = KEYS[environment];

const isTestEnv = import.meta.env.MODE === "test";

export const growthbook = new GrowthBook({
  apiHost: "https://cdn.growthbook.io",
  clientKey: GROWTHBOOK_CLIENT_KEY,
  enableDevMode: environment === "development",
  plugins: isTestEnv
    ? []
    : [
        autoAttributesPlugin(),
        thirdPartyTrackingPlugin({ trackers: ["gtag", "gtm"] }),
      ],
});

let initPromise: Promise<void> | null = null;

/**
 * Kick off (or join) the single feature-payload load. Fire-and-forget at app
 * boot. A failed load clears the memo so the next caller retries — memoizing
 * the rejection would otherwise poison every later call for the rest of the
 * session.
 */
export function initGrowthbook(): Promise<void> {
  if (initPromise === null) {
    initPromise = growthbook.init({ streaming: true }).then(
      () => undefined,
      (error: unknown) => {
        initPromise = null;
        throw error instanceof Error ? error : new Error(String(error));
      }
    );
  }
  return initPromise;
}

/**
 * The `uid` whose identity sync has settled, and how — `null` until it does,
 * and again while a different user's sync is in flight. On `failed`, GrowthBook
 * still buckets on the anonymous attributes.
 *
 * A cohort stable PER USER is only meaningful once GrowthBook hashes on that
 * user's id. Between mount and the `setAttributes` effect in `App`, flags are
 * still evaluated against the anonymous auto-attributes, so a read in that
 * window can bucket the same account differently on its next visit. Readers that
 * care (`usePracticeArm`) hold off until this matches the signed-in user.
 */
export const growthbookIdentityStore = memoryStore<{
  uid: string;
  status: "synced" | "failed";
} | null>(null);

declare global {
  interface Window {
    growthbook: typeof growthbook;
  }
}
window.growthbook = growthbook;
